import React, { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { httpClient } from '@/infrastructure/http/client/httpClient';
import { useAuth } from '@/presentation/contexts/AuthContext';

const tabClass = ({ isActive }: { isActive: boolean }) =>
  `inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
    isActive
      ? 'border-teal-500 text-teal-600 dark:text-teal-400'
      : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
  }`;

/**
 * Finanzas → Pagos: cobros recibidos y pagos informados por las clínicas.
 * Rutas hijas: /finanzas/pagos/recibidos, /finanzas/pagos/informados.
 */
export const PaymentsLayout: React.FC = () => {
  const { t } = useTranslation();
  const { canOperateBilling } = useAuth();
  const [pendingNotices, setPendingNotices] = useState(0);

  // Mismo contador que el menú: se refresca cuando la pestaña de informados aprueba o rechaza.
  useEffect(() => {
    if (!canOperateBilling) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const res = await httpClient.get<{ count: number }>('/panel-pagos/avisos/pendientes/count');
        if (!cancelled) setPendingNotices(Number(res.data?.count) || 0);
      } catch {
        // sin contador si falla
      }
    };
    void refresh();
    window.addEventListener('panel:payment-notices-changed', refresh);
    return () => {
      cancelled = true;
      window.removeEventListener('panel:payment-notices-changed', refresh);
    };
  }, [canOperateBilling]);

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
      <nav
        className="flex shrink-0 gap-0.5 overflow-x-auto border-b border-slate-200 px-3 pt-2 dark:border-gray-700 sm:px-3.5 md:px-5 xl:px-6"
        aria-label={t('menu.payments')}
      >
        <NavLink to="/finanzas/pagos/recibidos" className={tabClass}>
          {t('pages.payments.tabs.received')}
        </NavLink>
        <NavLink to="/finanzas/pagos/informados" className={tabClass}>
          {t('pages.payments.tabs.notices')}
          {pendingNotices > 0 ? (
            <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-amber-500 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white">
              {pendingNotices}
            </span>
          ) : null}
        </NavLink>
      </nav>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <Outlet />
      </div>
    </div>
  );
};
